import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useV2Auth } from '@/context/V2AuthContext';
import { Linkedin, Inbox, Send, Paintbrush, HelpCircle, Bell, BellOff, Heart, Sparkles, Pin } from 'lucide-react';
import CarmaImageModal from '@/components/CarmaImageModal';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export default function InboxV2() {
  const { user, API_V2, refresh } = useV2Auth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('received');

  useEffect(() => {
    document.body.classList.add('cc-no-overlays');
    return () => document.body.classList.remove('cc-no-overlays');
  }, []);
  const [receivedItems, setReceivedItems] = useState([]);
  const [sentItems, setSentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showGuideButton, setShowGuideButton] = useState(true);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushActivating, setPushActivating] = useState(false);
  const [imageCarma, setImageCarma] = useState(null);
  const [pinnedCarmaIds, setPinnedCarmaIds] = useState([]);

  useEffect(() => {
    if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  const requestPushPermission = async () => {
    if (!pushSupported || pushActivating) return;
    setPushActivating(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'denied') {
        toast.error(t('inbox.pushDenied'));
        setPushActivating(false);
        return;
      }
      if (permission !== 'granted') {
        setPushActivating(false);
        return;
      }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const vapidRes = await axios.get(`${API_V2}/push/vapid-key`);
      const vapidKey = vapidRes.data.vapidPublicKey;
      if (!vapidKey) {
        toast.error(t('inbox.pushError'));
        setPushActivating(false);
        return;
      }
      const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: raw });
      await axios.post(`${API_V2}/push/subscribe`, { subscription: sub.toJSON() }, { withCredentials: true });
      setPushEnabled(true);
      toast.success(t('inbox.pushSuccess'));
    } catch (err) { console.error('Push permission error:', err); toast.error(t('inbox.pushError')); } finally {
      setPushActivating(false);
    }
  };

  const shareToLinkedIn = (carma) => {
    const text = encodeURIComponent(`"${carma.message.substring(0, 200)}${carma.message.length > 200 ? '...' : ''}"\n\n– ${carma.senderName}\n\nCarmaCircle`);
    const url = encodeURIComponent('https://carmacircle.org');
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${text}`, '_blank', 'width=600,height=600');
  };

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    // Show welcome layer only if user has explicitly not seen it (DB flag)
    if (user.welcomeSeen === false) setShowWelcome(true);
    let cancelled = false;
    async function loadData() {
      setLoading(true); setError(null);
      try {
        const [receivedRes, sentRes, pinsRes] = await Promise.all([
          axios.get(`${API_V2}/carma/inbox`, { withCredentials: true }),
          axios.get(`${API_V2}/carma/sent`, { withCredentials: true }),
          axios.get(`${API_V2}/profile/pins`, { withCredentials: true }).catch(() => ({ data: { pinnedCarma: [] } })),
        ]);
        if (!cancelled) {
          setReceivedItems(receivedRes.data || []);
          setSentItems(sentRes.data || []);
          setPinnedCarmaIds((pinsRes.data.pinnedCarma || []).map((p) => p.id));
          // Onboarding modal for new registrations
          if (searchParams.get('onboarding') === '1') {
            setShowOnboarding(true);
            setSearchParams({}, { replace: true });
          }
        }
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 401) { navigate('/auth', { replace: true }); return; }
          setError(err.response?.data?.detail || t('common.error'));
        }
      }
      finally { if (!cancelled) setLoading(false); }
    }
    loadData();
    return () => { cancelled = true; };
  }, [user, API_V2, navigate, t, searchParams, setSearchParams]);

  const closeWelcome = async () => {
    setShowWelcome(false);
    try {
      await axios.post(`${API_V2}/profile/welcome-seen`, {}, { withCredentials: true });
      refresh();
    } catch (err) { console.error('Welcome-seen save failed:', err); }
  };

  const toggleReaction = async (messageId) => {
    setTogglingId(messageId); setError(null);
    try {
      const current = receivedItems.find((m) => m.id === messageId);
      const nextHasReacted = !current?.hasReacted;
      setReceivedItems((prev) => prev.map((m) => (m.id === messageId ? { ...m, hasReacted: nextHasReacted } : m)));
      await axios.post(`${API_V2}/carma/reactions/toggle`, { messageId }, { withCredentials: true });
    } catch (err) { setError(err.response?.data?.detail || t('common.error')); }
    finally { setTogglingId(null); }
  };

  const togglePinCarma = async (messageId) => {
    try {
      if (pinnedCarmaIds.includes(messageId)) {
        await axios.delete(`${API_V2}/profile/pin-carma/${messageId}`, { withCredentials: true });
        setPinnedCarmaIds((prev) => prev.filter((x) => x !== messageId));
        toast.success(t('innerstes.unpinSuccess'));
      } else {
        await axios.post(`${API_V2}/profile/pin-carma`, { messageId, anonymous: false }, { withCredentials: true });
        setPinnedCarmaIds((prev) => [...prev, messageId]);
        toast.success(t('innerstes.pinSuccess'));
      }
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  if (!user) return null;
  const items = activeTab === 'received' ? receivedItems : sentItems;

  return (
    <div className="min-h-screen text-[var(--text)]">
      <div className="max-w-3xl mx-auto px-4 py-8 relative">
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl p-6 shadow-sm">
        {showWelcome && (
          <div className="absolute inset-0 flex items-start justify-center bg-black/20 z-20 rounded-2xl">
            <div className="mt-16 w-full max-w-lg rounded-2xl border border-[var(--border)] bg-[var(--surface)] shadow-lg p-5 text-sm">
              <div className="flex justify-between items-start mb-3">
                <h2 className="text-base font-semibold text-[var(--text)]">{t('inbox.welcomeTitle')}</h2>
                <button type="button" onClick={closeWelcome} className="text-[var(--muted)] text-xs hover:text-[var(--text)]">{t('inbox.close')}</button>
              </div>
              <p className="text-[var(--muted)] mb-3">{t('inbox.welcomeText')}</p>
              <ol className="list-decimal pl-4 space-y-1 text-xs text-[var(--muted)]">
                <li>{t('inbox.welcomeStep1')}</li>
                <li>{t('inbox.welcomeStep2')}</li>
                <li>{t('inbox.welcomeStep3')}</li>
                <li>{t('inbox.welcomeStep4')}</li>
                <li>{t('inbox.welcomeStep5')}</li>
              </ol>
              <p className="text-xs text-[var(--muted)] mt-3">{t('inbox.welcomeFeedback')}</p>
            </div>
          </div>
        )}

        {/* Onboarding Modal — shown after first registration */}
        {showOnboarding && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-30 rounded-2xl" data-testid="onboarding-modal">
            <div className="w-full max-w-md rounded-2xl border border-[var(--primary)]/20 bg-white shadow-xl p-6 text-center mx-4">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-[var(--primary)]/10 flex items-center justify-center">
                  <Sparkles size={28} className="text-[var(--primary)]" />
                </div>
              </div>
              <h2 className="text-lg font-semibold text-[var(--text)] mb-2">{t('onboarding.title')}</h2>
              <p className="text-sm text-[var(--muted)] mb-1">{t('onboarding.subtitle')}</p>
              <p className="text-sm text-[var(--muted)] mb-6 leading-relaxed">{t('onboarding.text')}</p>
              <button
                type="button"
                onClick={() => { setShowOnboarding(false); navigate('/give'); }}
                data-testid="onboarding-cta-btn"
                className="w-full rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2.5 text-sm font-medium hover:opacity-90 inline-flex items-center justify-center gap-2 mb-2"
              >
                <Heart size={16} />
                {t('onboarding.ctaBtn')}
              </button>
              <button
                type="button"
                onClick={() => setShowOnboarding(false)}
                data-testid="onboarding-skip-btn"
                className="text-xs text-[var(--muted)] hover:text-[var(--text)]"
              >
                {t('onboarding.skipBtn')}
              </button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold">{t('inbox.title')}</h1>
            <p className="text-xs sm:text-sm text-[var(--muted)]">{t('inbox.description')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {pushSupported && (
              pushEnabled ? (
                <span className="rounded-full bg-green-50 border border-green-200 px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium text-green-700 inline-flex items-center gap-1.5" data-testid="push-enabled-badge">
                  <Bell size={13} />
                  {t('inbox.pushBtnEnabled')}
                </span>
              ) : (
                <button type="button" onClick={requestPushPermission} disabled={pushActivating} className="rounded-full bg-[var(--primary)]/10 border border-[var(--primary)]/30 px-3 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="push-enable-btn">
                  <BellOff size={13} />
                  {pushActivating ? t('inbox.pushActivating') : t('inbox.pushBtn')}
                </button>
              )
            )}
            <button type="button" onClick={() => navigate('/give')} className="rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium hover:opacity-90">{t('inbox.giveCarma')}</button>
            <button type="button" onClick={() => navigate('/profile')} className="rounded-full bg-[var(--surface)] border border-[var(--border)] px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium hover:bg-[var(--bg)]">{t('inbox.profileBtn')}</button>
          </div>
        </div>

        <div className="flex items-center justify-end mb-4">
          {showGuideButton && !showWelcome && (
            <button type="button" onClick={() => setShowWelcome(true)} className="flex-shrink-0 ml-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--surface)] border border-[var(--border)] px-3 py-1.5 text-[11px] font-medium text-[var(--muted)] hover:bg-[var(--bg)] transition-colors">
              <HelpCircle size={13} />
              {t('inbox.betaGuide')}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-[var(--border)]">
          <button type="button" data-testid="inbox-tab-received" onClick={() => setActiveTab('received')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'received' ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--muted)] hover:text-[var(--text)]'}`}>
            <Inbox size={16} /><span>{t('inbox.received')}</span>
            {receivedItems.length > 0 && <span className="bg-[var(--primary)]/10 text-[var(--primary)] text-xs px-2 py-0.5 rounded-full">{receivedItems.length}</span>}
          </button>
          <button type="button" data-testid="inbox-tab-sent" onClick={() => setActiveTab('sent')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'sent' ? 'border-[var(--primary)] text-[var(--primary)]' : 'border-transparent text-[var(--muted)] hover:text-[var(--text)]'}`}>
            <Send size={16} /><span>{t('inbox.sent')}</span>
            {sentItems.length > 0 && <span className="bg-[var(--muted)]/10 text-[var(--muted)] text-xs px-2 py-0.5 rounded-full">{sentItems.length}</span>}
          </button>
        </div>

        {loading && <div className="text-sm text-[var(--muted)]">{t('inbox.loading')}</div>}
        {error && <div className="text-xs text-[var(--danger)] mb-2">{error}</div>}
        {!loading && items.length === 0 && activeTab === 'received' && (
          <div className="py-10 text-center" data-testid="empty-inbox-hint">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[var(--primary)]/5 border-2 border-dashed border-[var(--primary)]/20 flex items-center justify-center">
                <Heart size={24} className="text-[var(--primary)]/40" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-[var(--text)] mb-1">{t('onboarding.emptyInboxTitle')}</h3>
            <p className="text-sm text-[var(--muted)] mb-4 max-w-xs mx-auto">{t('onboarding.emptyInboxText')}</p>
            <button
              type="button"
              onClick={() => navigate('/give')}
              data-testid="empty-inbox-give-btn"
              className="rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] px-5 py-2 text-sm font-medium hover:opacity-90 inline-flex items-center gap-2"
            >
              <Send size={14} />
              {t('onboarding.emptyInboxBtn')}
            </button>
          </div>
        )}
        {!loading && items.length === 0 && activeTab === 'sent' && (
          <div className="text-sm text-[var(--muted)] py-8 text-center">{t('inbox.emptySent')}</div>
        )}

        <ul className="space-y-3">
          {activeTab === 'received' && receivedItems.map((m) => (
            <li key={m.id} className="border border-[var(--border)] rounded-xl bg-white/70 px-4 py-3 text-sm">
              <div className="flex justify-between gap-3">
                <div className="flex-1">
                  <div className="text-xs text-[var(--muted)] mb-1">{t('inbox.from')} {m.senderName || t('give.unknown')} · {new Date(m.createdAt).toLocaleString()}</div>
                  <div className="text-sm mb-1">{m.message}</div>
                  {m.context && <div className="text-xs text-[var(--muted)]">{t('inbox.context')} {m.context}</div>}
                </div>
                <button type="button" onClick={() => toggleReaction(m.id)} disabled={togglingId === m.id} className={`self-start mt-1 rounded-full border px-3 py-1 text-xs font-medium flex items-center gap-1 ${m.hasReacted ? 'border-[var(--primary)] text-[var(--primary)] bg-[var(--bg)]' : 'border-[var(--border)] text-[var(--muted)] bg-[var(--surface)]'}`}>
                  <span>{m.hasReacted ? '\u2764\uFE0F' : '\uD83E\uDD0D'}</span>
                  <span className="hidden sm:inline">{m.hasReacted ? t('inbox.unreact') : t('inbox.react')}</span>
                </button>
              </div>
              <div className="mt-3 pt-2 border-t border-[var(--border)] flex items-center gap-2">
                <span className="text-xs text-[var(--muted)]">{t('inbox.share')}</span>
                <button type="button" onClick={() => shareToLinkedIn(m)} className="flex items-center gap-1 text-xs text-[#0077b5] hover:opacity-80 px-2 py-1 rounded border border-[#0077b5]/30 bg-[#0077b5]/5" title="LinkedIn"><Linkedin size={14} /><span>{t('inbox.linkedin')}</span></button>
                <button type="button" onClick={() => setImageCarma({ message: m.message, senderName: m.senderName, receiverName: user?.name, context: m.context, createdAt: m.createdAt })} className="flex items-center gap-1 text-xs text-[var(--primary)] hover:opacity-80 px-2 py-1 rounded border border-[var(--primary)]/30 bg-[var(--primary)]/5" data-testid={`share-image-btn-${m.id}`} title={t('inbox.image')}><Paintbrush size={14} /><span>{t('inbox.image')}</span></button>
                <button type="button" onClick={() => togglePinCarma(m.id)} className={`flex items-center gap-1 text-xs px-2 py-1 rounded border ${pinnedCarmaIds.includes(m.id) ? 'text-[var(--primary)] border-[var(--primary)]/50 bg-[var(--primary)]/10' : 'text-[var(--muted)] border-[var(--border)] bg-[var(--surface)] hover:text-[var(--primary)]'}`} data-testid={`pin-carma-btn-${m.id}`} title={t('innerstes.pin')}><Pin size={14} className={pinnedCarmaIds.includes(m.id) ? 'fill-[var(--primary)]' : ''} /><span>{pinnedCarmaIds.includes(m.id) ? t('innerstes.pinned') : t('innerstes.pin')}</span></button>
              </div>
            </li>
          ))}

          {activeTab === 'sent' && sentItems.map((m) => (
            <li key={m.id} className="border border-[var(--border)] rounded-xl bg-white/70 px-4 py-3 text-sm">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-xs text-[var(--muted)] mb-1">
                  <span>{t('inbox.to')} {m.receiverName || t('give.unknown')}</span>
                  <span>·</span>
                  <span>{new Date(m.createdAt).toLocaleString()}</span>
                  {m.status === 'pending' && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200" data-testid={`status-pending-${m.id}`}>{t('inbox.statusPending')}</span>}
                  {m.status === 'viewed' && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 border border-sky-200" data-testid={`status-viewed-${m.id}`}>{t('inbox.statusViewed')}</span>}
                  {m.status === 'claimed' && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200" data-testid={`status-claimed-${m.id}`}>{t('inbox.statusClaimed')}</span>}
                  {m.status === 'delivered' && <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-[var(--primary)] border border-blue-200" data-testid={`status-delivered-${m.id}`}>{t('inbox.statusDelivered')}</span>}
                  {m.type === 'invite' && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--surface)] text-[var(--muted)] border border-[var(--border)]">{t('inbox.typeInvite')}</span>}
                  {m.type === 'shared_link' && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--surface)] text-[var(--muted)] border border-[var(--border)]">{t('inbox.typeLink')}</span>}
                </div>
                <div className="text-sm mb-1">{m.message}</div>
                {m.context && <div className="text-xs text-[var(--muted)]">{t('inbox.context')} {m.context}</div>}
                {m.reply && (
                  <div className="mt-2 border-l-2 border-[var(--primary)]/40 pl-3 py-1" data-testid={`shared-reply-${m.id}`}>
                    <p className="text-xs text-[var(--muted)] mb-0.5">{m.receiverName} hat geantwortet:</p>
                    <p className="text-sm italic text-[var(--text)]/85">„{m.reply.message}“</p>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-2 border-t border-[var(--border)] flex items-center gap-2">
                <span className="text-xs text-[var(--muted)]">{t('inbox.share')}</span>
                <button type="button" onClick={() => { const txt = encodeURIComponent(`"${m.message.substring(0, 200)}${m.message.length > 200 ? '...' : ''}"\n\nCarmaCircle`); const url = encodeURIComponent('https://carmacircle.org'); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&summary=${txt}`, '_blank', 'width=600,height=600'); }} className="flex items-center gap-1 text-xs text-[#0077b5] hover:opacity-80 px-2 py-1 rounded border border-[#0077b5]/30 bg-[#0077b5]/5" title="LinkedIn"><Linkedin size={14} /><span>{t('inbox.linkedin')}</span></button>
                <button type="button" onClick={() => setImageCarma({ message: m.message, senderName: user?.name, receiverName: m.receiverName, context: m.context, createdAt: m.createdAt })} className="flex items-center gap-1 text-xs text-[var(--primary)] hover:opacity-80 px-2 py-1 rounded border border-[var(--primary)]/30 bg-[var(--primary)]/5" data-testid={`share-image-sent-btn-${m.id}`} title={t('inbox.image')}><Paintbrush size={14} /><span>{t('inbox.image')}</span></button>
              </div>
            </li>
          ))}
        </ul>
        </div>
      </div>
      {imageCarma && <CarmaImageModal carma={imageCarma} onClose={() => setImageCarma(null)} />}
    </div>
  );
}
